// src/core/hooks/useLiveSession.ts
import { useEffect, useState } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { rtdb } from '../../firebase';
import { useSession } from '../context/SessionContext';
export interface LiveSession {
  sessionId: string; // id del box en estos momentos
  idBox: string;
  wodId: number;
  elementId: number;
  expanded: boolean;
  fullscreen: boolean;
}

export function useLiveSession() {
  const { sessionId } = useSession();
  const [session, setSession] = useState<LiveSession | null>(null);

  // 🔧 DEBUG: Solo en desarrollo, estado para control manual
  const [debugState, setDebugState] = useState<LiveSession | null>(null);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    if (!sessionId) return; // aún no hay sessionId

    // MODO DEBUG LOCAL (Manual)
    if (debugMode && debugState) {
      setSession(debugState);
      return;
    }

    // MODO TESTING LOCAL - Solo disponible en desarrollo
    if (process.env.NODE_ENV === 'development') {
      const TESTING_MODE = false; // CAMBIADO A FALSE para permitir pruebas reales con la App Móvil

      if (TESTING_MODE) {
        // Simula datos locales para testing
        const mockSession = {
          sessionId,
          idBox: sessionId,
          wodId: 1,
          elementId: 17712, // ID del bloque que quieres expandir
          expanded: true, // true = expandido, false = normal
          fullscreen: false, // true = pantalla completa, false = modal normal
        } as LiveSession;

        setSession(mockSession);
        return;
      }
    }

    // FIREBASE REAL - Funcionalidad normal de producción
    const path = `live_sessions/${sessionId}`;
    const sessionRef = ref(rtdb, path);

    console.log(`[useLiveSession] Conectando a Firebase path: ${path}`);

    const unsubscribe = onValue(
      sessionRef,
      (snapshot) => {
        const data = snapshot.val();
        console.log('[useLiveSession] Datos recibidos de Firebase:', data);
        if (data) {
          setSession({
            sessionId,
            idBox: data.idBox || sessionId,
            wodId: data.wodId ? Number(data.wodId) : 0,
            elementId: data.elementId ? Number(data.elementId) : 0,
            expanded: Boolean(data.expanded),
            fullscreen: Boolean(data.fullscreen),
          } as LiveSession);
        } else {
          setSession(null);
        }
      },

      (error) => {
        console.error('[useLiveSession] Error en RTDB:', error);
        setSession(null);
      },
    );


    return () => {
      off(sessionRef);
      unsubscribe();
    };
  }, [sessionId, debugMode, debugState]);

  // DEBUG: Función para control externo (solo en desarrollo)
  const updateDebugState =
    process.env.NODE_ENV === 'development'
      ? (elementId: number, expanded: boolean, fullscreen: boolean) => {
        setDebugState({
          sessionId,
          idBox: sessionId,
          wodId: 1,
          elementId,
          expanded,
          fullscreen,
        } as LiveSession);
        setDebugMode(true);
      }
      : undefined;

  // En desarrollo: retorna session + función de control
  // En producción: solo retorna session (updateDebugState es undefined)
  return process.env.NODE_ENV === 'development' ? { session, updateDebugState } : session;
}
